import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cn } from "../../utils"
import { Label } from "./input/Label"

const RadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
    label?: string
    success?: boolean
    error?: boolean
    containerClassName?: string
    tooltip?: string
  }
>(
  (
    {
      className,
      label,
      error,
      success,
      required,
      containerClassName,
      tooltip,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={cn("flex flex-col gap-2", containerClassName)}>
        {label && (
          <Label
            error={error}
            success={success}
            tooltip={tooltip}
            className="text-left"
          >
            {label}
            {required && <span className="text-red">*</span>}
          </Label>
        )}
        <RadioGroupPrimitive.Root
          className={cn("grid gap-2", className)}
          {...props}
          ref={ref}
        />
      </div>
    )
  },
)
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const RadioGroupItem = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "aspect-square size-4 rounded-full border border-primary-accent-blue text-primary-green ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex item-center justify-center data-[state=checked]:border-primary-green",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <span className="rounded-full bg-current w-2.5 h-2.5 " />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})

interface RadioBooleanFieldProps {
  label: string
  required?: boolean
  value?: boolean | null
  onChange: (value: boolean | null) => void
  className?: string
  disabled?: boolean
  labels?: { true: string; false: string }
}

export function RadioBooleanField({
  label,
  required,
  value,
  onChange,
  className,
  disabled,
  labels = { true: "Ja", false: "Nee" },
}: RadioBooleanFieldProps) {
  return (
    <RadioGroup
      value={value === true ? "ja" : value === false ? "nee" : ""}
      onValueChange={(val) =>
        onChange(val === "ja" ? true : val === "nee" ? false : null)
      }
      label={label}
      required={required}
      className={className ?? "flex flex-row gap-6"}
      disabled={disabled}
    >
      {(
        [
          { val: "ja", labelText: labels.true },
          { val: "nee", labelText: labels.false },
        ] as const
      ).map(({ val, labelText }) => (
        <label key={val} className="flex items-center gap-1">
          <RadioGroupItem value={val} disabled={disabled} /> {labelText}
        </label>
      ))}
    </RadioGroup>
  )
}

RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
